import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import DashboardHeader from "../../../components/Other/DashboardHeader";
import PageContainer from "../../../components/pages/PageContainer";
import LeagueJoinPreview, { landingContainerClass } from "../landing/LeagueJoinPreview";
import usePullToRefresh from "../../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../../components/Other/PullToRefreshIndicator";

// "League details" from the in-league three-dot menu.
//
// The same card a joiner is shown — art, dates, members, the rules they agreed to,
// what the podium pays — minus the join/cancel footer, because they're already in.
// Reusing that card is the point: what the league promised at the door is exactly
// what it reports from the inside.
//
// Client Rev 3: opening this from inside a league must NOT drop the user into the
// external landing state. So the screen is focused — one back arrow, no QUSHi
// logo or main three-dot menu, no bottom app navigation (both suppressed via
// isLeagueDetailsPath) — and Back goes straight to the league's Home rather than
// wherever history happens to point.
// The /contest address already sits inside Contest's PageContainer; the
// standalone one does not, so it wraps itself in the identical component. A
// Fragment for the first case means neither address gets two.
const LandingSurface = ({ standalone, children }) =>
  standalone ? <PageContainer>{children}</PageContainer> : <>{children}</>;

const LeagueDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  // The same screen, at a second address, for someone who is NOT in the league.
  //
  // Client: "'Game details' will take the user to the game details preview screen
  // so they can see what the competition involves. There is no CTA on a league or
  // game preview only details page. its a focus screen with only the back arrow."
  //
  // That is a description of THIS screen — one back arrow, no app chrome, and
  // LeagueJoinPreview rendered with showJoinCta={false}. The only thing standing
  // between a non-member and it was the membership redirect below, which exists
  // because /contest/<slug>/details is a league-interior route. So rather than
  // build a second details page that would immediately start drifting from this
  // one, the same component is mounted at /leaderboards/<slug>/details, where
  // the app shell stays QUSHi's own (see utils/leaguePath.js) and the gate is
  // lifted.
  const { pathname } = useLocation();
  // TWO DIFFERENT OUTLET CONTEXTS reach this screen, and only one is an array.
  //
  // /leaderboards/<slug>/details sits under AuthLayout, which passes
  // `[sidebarToggle]`. /contest/<slug>/details sits under Contest, which passes
  // `{ search, handleChange }`. Array-destructuring the object threw "Invalid
  // attempt to destructure non-iterable instance" and the error boundary
  // swallowed the whole screen — the in-competition League details link showed
  // "Something went wrong". `|| []` did not save it: an object is truthy, so the
  // fallback never ran.
  //
  // Only the standalone address renders DashboardHeader, and that is the one
  // whose context IS the array, so reading it defensively costs nothing.
  const outletContext = useOutletContext();
  const sidebarToggle = Array.isArray(outletContext) ? outletContext[0] : undefined;
  const standalone = pathname.startsWith("/leaderboards/");
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  // Same derivation LeagueSettings uses to decide the organiser, so the two
  // screens cannot disagree about who owns the league.
  const myId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null")?.id ?? null;
    } catch (e) {
      return null;
    }
  }, []);
  const isOrganizer = !!game && !!myId && String(game.user_id) === String(myId);

  const refreshFunction = useCallback(async () => {
    try {
      const res = await getGameFromSlug({ slug });
      const data = res?.data?.success === false ? null : res?.data?.data;
      if (data?.id) {
        setGame(data);
      }
    } catch (error) {
      console.error("Error refreshing league details:", error);
    }
  }, [slug]);

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshFunction,
    disabled: false,
  });

  useEffect(() => {
    let cancelled = false;
    setResolved(false);
    (async () => {
      try {
        const res = await getGameFromSlug({ slug });
        const data = res?.data?.success === false ? null : res?.data?.data;
        if (!cancelled) setGame(data?.id ? data : null);
      } catch (e) {
        console.error("League details lookup failed:", e);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!resolved) return;
    if (!game) {
      navigate("/contest", { replace: true });
      return;
    }
    // A league's own screens are for MEMBERS. Someone who hasn't joined belongs on
    // the league's front door — which decides between the join preview and the
    // invite gate — not inside its details.
    //
    // ...except at the standalone address, which is FOR non-members: it is where
    // the leaderboard's "Game details ›" sends a visitor who cannot go inside.
    if (!standalone && !game.isMember) {
      navigate(`/contest/${game.vanity_url}`, { replace: true });
    }
  }, [resolved, game, navigate, standalone]);

  if (!resolved) {
    return (
      <div className="mt-16 md:mt-24 pb-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="h-36 md:h-44 rounded-2xl bg-[#F3F4F6] animate-pulse" />
        </div>
      </div>
    );
  }
  if (!game) return null;
  if (!standalone && !game.isMember) return null;

  // A member arrived from the league's own three-dot menu, where history can
  // point anywhere, so Back is an explicit path to the league's Home. A visitor
  // arrived from the leaderboard, so Back is the screen they came from — the
  // same rule the standalone leaderboard's own arrow follows.
  const backToLeagueHome = () => {
    if (standalone) {
      if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
      else navigate("/contest");
      return;
    }
    navigate(`/contest/${game.vanity_url}`);
  };

  /**
   * Share, from the screen a member is actually standing on.
   *
   * Client: "I just realized Public League organizers can't share their league...
   * the organizer can only view the league through the League Details screen,
   * which doesn't have a native Share feature. The same issue applies to anyone
   * who already joined the league. Once they are inside, they can't easily get
   * back to the original landing page to share it. Please add the native Share
   * feature to the League Details screen so organizers and joined users can
   * share the Public League from there."
   *
   * WHO SEES IT differs by league type, because the client drew that line
   * themselves: "Public should be different from Private here. We want anyone to
   * be able to share a Public League." So a public league's control is open to
   * every member who reached this screen, and a private league's stays with the
   * organiser — a private league is joined by invitation, and handing every
   * member a working invite link would quietly make it not private.
   *
   * The message and the address come from utils/shareMessage, the same source
   * the menu row and the landing card use: "all three share places… use the same
   * setup for image and text copy."
   */
  // ...at BOTH addresses. This was briefly taken off the standalone preview on
  // the reading that "back arrow, no CTA" meant nothing but the arrow in the
  // header; the client corrected it: "Landing Pages, even in details, are
  // shareable always (excluding private leagues). Easy ways to share QUSHi all
  // day." Share is not a CTA — it does not enter or join the competition — so
  // the rule below is the only one that governs it, at either address.
  const isPrivate = String(game.game_type || "").toLowerCase() === "private";
  const canShare = !isPrivate || isOrganizer;

  return (
    <>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div {...containerProps}>
        {/* THE FOCUSED HEADER IS GONE, and with it the divider, the back
            arrow and the share icon it carried.

            Client, on the landing page previews: "desktop - restore desktop
            header with QUSHi logo, notifications, and chats icons. Remove
            header divider line, delete arrow, move share icon down inline with
            the arrow and align it to the right edge of the content… Mobile is
            the arrow QUSHi logo and share icon."

            WHY THE DESKTOP HEADER LOOKED MISSING. It was never removed —
            Contest already renders DashboardHeader at this width. WizardHeader
            is `fixed inset-x-0 z-40` with a white ground, so it sat ON TOP of
            the app header and hid the logo, the bell and the chats icon behind
            it. Taking it away restores them rather than rebuilding them.

            The back arrow and the share control both live in the content column
            now — LeaguePlayTopBar, the same row every League Play landing page
            uses — which is what "inline with the arrow and align it to the right
            edge of the content" describes. On a phone the app header supplies
            arrow, logo and share instead; see isLeagueDetailsPath. */}
        {/* The standalone address is NOT under /contest, so nothing above it
            renders the app header. It does its own, exactly as the standalone
            leaderboard does. */}
        {standalone && (
          <DashboardHeader
            toggle={sidebarToggle}
            /* This address is not a league path, so the header cannot resolve
               the competition itself — see `shareGame`. Only when sharing is
               allowed, so a private league's link is not handed out by the one
               screen that forgot to check. */
            shareGame={canShare ? game : null}
          />
        )}

      {/* THE LANDING PAGE, WHOLE — its own header card included.
          
          Client: "When I click on Game details from inside the competition left
          menu or three-dot menu, it should show the approved design landing page
          without the CTA. Right now, it cuts off the landing page header card
          and ads in the game competitions home page header... this page should be
          the landing page without CTA, header area is back arrow and share only."
          And for the other entry point: "Please check the League details
          leaderboard preview link for desktop and mobile. This link should go to
          the Game Landing page, without the Game Home screen header... Both Game
          details links (paths) should go to the landing page with no CTA."

          WHAT CHANGED. This used to wrap the card in LeagueShell and pass
          `identityInShell`, which is the exact pair the client is describing:
          LeagueShell printed the in-league identity header (the eyebrow, name,
          HOSTED BY and countdown — the block circled in their screenshot), and
          `identityInShell` told the landing card to DROP its own header card so
          the two would not both appear. The result was the landing page with its
          designed header replaced by the Game Home one.

          So the shell is gone and the card renders its own identity: this is now
          the landing page exactly as designed, minus the CTA. The container
          mirrors LeagueEntry's — the front door that renders the same card — so
          the details screen and the real landing page cannot end up different
          widths.

          `showJoinCta={false}` stays: the reader is already inside the
          competition, so there is nothing left to join. Share is not a CTA and
          stays in the header above. */}
      {/* THE SAME SURFACE THE /contest ADDRESS GETS.
          
          Client: "When I click League/Game details from the leaderboard
          preview, it's still all messed up. Different background color, position
          is off, width may be off - this page should look exactly like the main
          landing page."
          
          All three were the same cause. /contest/<slug>/details renders inside
          Contest, which wraps its Outlet in PageContainer; the standalone
          address sits directly under AuthLayout and got none of it. PageContainer
          is what supplies the `md:ml-64` sidebar gutter (without it the content
          column centred on the WHOLE viewport and sat 128px to the left), the
          white ground (without it the page showed AuthLayout's grey
          `.adminLayout` through) and the px-2 padding (which is the mobile
          width difference).
          
          So the standalone branch borrows the same component rather than
          restating its three values, and the two addresses cannot drift.

          Client: "League details (desktop) still not right with the grey line."
          The header clearance was a MARGIN (mt-16 md:mt-24) on the first child
          of PageContainer. PageContainer has no top padding or border, so that
          margin collapsed through it: the white container itself started 96px
          down, and the strip between the fixed header's bottom and that point
          showed AuthLayout's grey .adminLayout. PADDING does not collapse — the
          white ground now starts at the top and the content sits exactly where
          it did. Same reason the standalone leaderboard (pt-20 md:pt-24) never
          had the band. */}
      <LandingSurface standalone={standalone}>
      <div className="pt-16 md:pt-24 pb-0 md:pb-12">
        <div className={landingContainerClass(game)}>
          {/* `showShare` drives LeaguePlayTopBar's icon — the content column's
              right edge, which is where the client moved it to. The same rule
              decides it as before: anyone may share a public league or a cash
              game, a private league only its organiser. */}
          <LeagueJoinPreview game={game} showJoinCta={false} showShare={canShare} />
        </div>
      </div>
      </LandingSurface>
      </div>
    </>
  );
};

export default LeagueDetails;
